import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { Plus, Shield, Pencil, Trash2, Lock, Users as UsersIcon, Search } from 'lucide-react';
import platformAdminService from '../../services/platformAdminService';
import PermissionCheckboxGroup from './PermissionCheckboxGroup';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { ROLE_NAMES, RESERVED_ROLE_NAMES } from '../../constants/permissions';
import { EMPTY_PARTNER_INDEX, companyLabel } from '../../utils/tenancy';
import PaginationBar from './PaginationBar';
import styles from './PlatformAdmin.module.scss';

const PAGE_SIZE = 50;

const isWildcard = (role) => (role.permission_keys || []).includes('*');
// platform_admin / company_admin / partner_admin are fully read-only. Other
// system roles allow editing permission_keys only; custom roles allow everything.
// partner_admin carries the partner_* keys, which exist in no other role.
const isFullyReadOnly = (role) =>
  role.is_system &&
  (role.name === ROLE_NAMES.PLATFORM_ADMIN ||
    role.name === ROLE_NAMES.COMPANY_ADMIN ||
    role.name === ROLE_NAMES.PARTNER_ADMIN ||
    isWildcard(role));

const emptyForm = {
  id: null,
  name: '',
  description: '',
  scope: 'company',
  company_id: '',
  permission_keys: [],
};

/**
 * Roles panel (spec §4.4): create/edit/delete roles in both the platform and
 * company tiers. Permission checkboxes come from the live catalog (§4.6) —
 * only `platform` and `company` scope keys; partner keys come only from the
 * partner_admin role and can't go into a custom role (Partner tier guide §3.3).
 */
const RolesPanel = ({ notify, catalog, partnerIndex = EMPTY_PARTNER_INDEX }) => {
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [dialog, setDialog] = useState(null); // { mode: 'create'|'edit', ...form } via `form`
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // { role }
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  // Companies feed the create-role company selector — load once (big page).
  const loadCompanies = useCallback(async () => {
    try {
      const companyData = await platformAdminService.listCompanies(false, { limit: 200 });
      setCompanies(companyData?.companies || (Array.isArray(companyData) ? companyData : []));
    } catch (err) {
      notify(err?.message || 'Failed to load companies.', 'error');
    }
  }, [notify]);

  const loadRoles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await platformAdminService.listRoles({
        includePlatformTier: true,
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      const list = Array.isArray(data) ? data : data?.roles || [];
      setRoles(list);
      setTotal(Number.isFinite(data?.total) ? data.total : list.length);
    } catch (err) {
      setError(err?.message || 'Failed to load roles.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, offset]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const catalogForScope = useCallback(
    (scope) => (catalog || []).filter((p) => p.scope === scope),
    [catalog]
  );

  const companyName = useCallback(
    (id) => {
      if (!id) return null;
      return companyLabel(id, companies.find((c) => c.id === id)?.name || `Company ${id}`, { partnerIndex });
    },
    [companies, partnerIndex]
  );

  const openCreate = () => {
    setForm({ ...emptyForm });
    setDialog({ mode: 'create' });
  };

  const openEdit = (role) => {
    setForm({
      id: role.id,
      name: role.name,
      description: role.description || '',
      scope: role.scope,
      company_id: role.company_id || '',
      permission_keys: role.permission_keys || [],
    });
    setDialog({ mode: 'edit', role });
  };

  const togglePerm = (key) => {
    setForm((prev) => ({
      ...prev,
      permission_keys: prev.permission_keys.includes(key)
        ? prev.permission_keys.filter((k) => k !== key)
        : [...prev.permission_keys, key],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    const isEdit = dialog?.mode === 'edit';
    const readOnly = isEdit && dialog.role && isFullyReadOnly(dialog.role);
    const nameLocked = isEdit && dialog.role?.is_system; // system roles can't be renamed
    try {
      setSaving(true);
      if (isEdit) {
        const payload = { permission_keys: form.permission_keys };
        if (!nameLocked) {
          payload.name = form.name.trim();
          payload.description = form.description.trim();
        }
        if (readOnly) {
          // Nothing editable — shouldn't reach here (button hidden), guard anyway.
          setDialog(null);
          return;
        }
        await platformAdminService.updateRole(form.id, payload);
        notify('Role updated.', 'success');
      } else {
        if (RESERVED_ROLE_NAMES.includes(form.name.trim().toLowerCase())) {
          notify(`"${form.name.trim()}" is a built-in role name. Choose another name.`, 'error');
          return;
        }
        const payload = {
          name: form.name.trim(),
          description: form.description.trim(),
          scope: form.scope,
          permission_keys: form.permission_keys,
          company_id: form.scope === 'company' && form.company_id ? form.company_id : null,
        };
        await platformAdminService.createRole(payload);
        notify('Role created.', 'success');
      }
      setDialog(null);
      await loadRoles();
    } catch (err) {
      notify(err?.data?.detail || err?.message || 'Failed to save role.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm?.role) return;
    try {
      setBusy(true);
      const res = await platformAdminService.deleteRole(confirm.role.id);
      const affected = res?.affected_users ?? 0;
      notify(`Role deleted.${affected ? ` ${affected} user(s) lost this role.` : ''}`, 'info');
      setConfirm(null);
      await loadRoles();
    } catch (err) {
      notify(err?.data?.detail || err?.message || 'Failed to delete role.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const sortedRoles = useMemo(() => {
    return [...roles].sort((a, b) => {
      if (a.scope !== b.scope) return a.scope === 'platform' ? -1 : 1;
      if (a.is_system !== b.is_system) return a.is_system ? -1 : 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [roles]);

  const dialogReadOnly = dialog?.mode === 'edit' && dialog.role && isFullyReadOnly(dialog.role);
  const dialogNameLocked = dialog?.mode === 'edit' && dialog.role?.is_system;
  const activeScope = form.scope;

  return (
    <>
      <Box className={styles.toolbar}>
        <TextField
          size="small"
          placeholder="Search roles…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" startIcon={<Plus size={18} />} className={styles.primaryBtn} onClick={openCreate}>
          New role
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Box className={styles.tableCard}>
          {sortedRoles.map((role) => {
            const fullyRO = isFullyReadOnly(role);
            return (
              <Box key={role.id} className={styles.userRow} sx={{ flexWrap: 'wrap' }}>
                <Box className={styles.companyMain} sx={{ flex: '1 1 240px' }}>
                  <span className={styles.companyAvatar}><Shield size={18} /></span>
                  <Box>
                    <span className={styles.companyName}>
                      {role.name}
                      {role.company_id && companyName(role.company_id) && (
                        <span className={styles.companyMeta}> · {companyName(role.company_id)}</span>
                      )}
                    </span>
                    <span className={styles.companyMeta}>{role.description || '—'}</span>
                  </Box>
                </Box>

                <span className={styles.scopeBadge}>{role.scope}</span>
                <span className={`${styles.badge} ${role.is_system ? styles.badgeSystem : ''}`}>
                  {role.is_system ? 'System' : 'Custom'}
                </span>
                <span className={styles.badge}>
                  {isWildcard(role) ? 'All access' : `${(role.permission_keys || []).length} areas`}
                </span>

                <Box className={styles.actionsCell}>
                  {!fullyRO && (
                    <Tooltip title="Edit">
                      <IconButton size="small" className={styles.subtleBtn} onClick={() => openEdit(role)}>
                        <Pencil size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {!role.is_system && (
                    <Tooltip title="Delete">
                      <IconButton size="small" className={styles.dangerIcon} onClick={() => setConfirm({ role })}>
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {fullyRO && (
                    <Tooltip title="Read-only role">
                      <span className={styles.muted} style={{ padding: '0 8px' }}>Read-only</span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {total > 0 && (
        <PaginationBar
          total={total}
          limit={PAGE_SIZE}
          offset={offset}
          loading={loading}
          unit="roles"
          onOffsetChange={setOffset}
        />
      )}

      {/* Create / edit role */}
      <Dialog open={!!dialog} onClose={() => !saving && setDialog(null)} classes={{ paper: styles.dialogPaper }} maxWidth="md" fullWidth>
        <DialogTitle className={styles.dialogTitle}>
          {dialog?.mode === 'edit' ? `Edit role — ${dialog?.role?.name}` : 'New role'}
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <TextField
            autoFocus
            label="Role name"
            fullWidth
            variant="outlined"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            disabled={dialogNameLocked || dialogReadOnly}
            className={styles.formField}
          />
          <TextField
            label="Description"
            fullWidth
            variant="outlined"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            disabled={dialogNameLocked || dialogReadOnly}
            className={styles.formField}
          />

          {dialog?.mode === 'create' && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Scope</InputLabel>
                <Select
                  label="Scope"
                  value={form.scope}
                  onChange={(e) => setForm({ ...form, scope: e.target.value, permission_keys: [], company_id: '' })}
                >
                  <MenuItem value="company">Company</MenuItem>
                  <MenuItem value="platform">Platform</MenuItem>
                </Select>
              </FormControl>
              {form.scope === 'company' && (
                <FormControl fullWidth>
                  <InputLabel>Company (optional)</InputLabel>
                  <Select
                    label="Company (optional)"
                    value={form.company_id}
                    onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                  >
                    <MenuItem value="">Global template (no company)</MenuItem>
                    {companies.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{companyName(c.id)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Permissions ({activeScope} scope)
            </Typography>
            {dialogReadOnly ? (
              <Box className={styles.noteBanner}>
                <Lock size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>This is a built-in all-access role and can't be edited.</span>
              </Box>
            ) : (
              <PermissionCheckboxGroup
                catalog={catalogForScope(activeScope)}
                selected={form.permission_keys}
                onToggle={togglePerm}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setDialog(null)} disabled={saving} className={styles.cancelButton}>Cancel</Button>
          {!dialogReadOnly && (
            <Button variant="contained" onClick={handleSave} disabled={saving || !form.name.trim()} className={styles.primaryBtn}>
              {saving ? <CircularProgress size={20} color="inherit" /> : dialog?.mode === 'edit' ? 'Save changes' : 'Create role'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Delete role</DialogTitle>
        <DialogContent className={styles.deleteContent}>
          <Box className={styles.warningIconWrap}><UsersIcon /></Box>
          <Typography className={styles.deleteHeading}>Delete "{confirm?.role?.name}"?</Typography>
          <Typography className={styles.deleteText}>
            Any users holding this role will lose it immediately. This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setConfirm(null)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleDelete} disabled={busy} className={styles.dangerButton}>
            {busy ? 'Working...' : 'Delete role'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RolesPanel;
